import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Pencil,
  MapPin,
  ReceiptText,
  Truck,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  customersService,
  type AddressCreatePayload,
  type AddressUpdatePayload,
} from '@/services/customers';
import { cn } from '@/lib/utils';
import type { CustomerAddress, CustomerAddressRole } from '@/types/customers';

interface Props {
  customerId: string | null;
}

/**
 * Per-customer address book. Each row carries a role (BILLING / SHIPPING /
 * BOTH) and optional default flags. Default toggles are mutually exclusive
 * per role and the BE enforces the demote on toggle so flipping a default
 * is always atomic.
 *
 * Drives the customer detail dialog's Addresses tab; once invoices land
 * (Slice 13) the same data feeds invoice "Bill to" / "Ship to" defaults
 * and the GST intra-vs-inter-state classification.
 */
export function CustomerAddressesSection({ customerId }: Props) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomerAddress | null>(null);

  const addressesQuery = useQuery({
    queryKey: ['customers', 'addresses', customerId],
    queryFn: () => customersService.listAddresses(customerId!),
    enabled: !!customerId,
  });

  const remove = useMutation({
    mutationFn: (addressId: string) => customersService.deleteAddress(customerId!, addressId),
    onSuccess: () => {
      toast.success('Address removed');
      qc.invalidateQueries({ queryKey: ['customers', 'addresses', customerId] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete address'),
  });

  const setDefault = useMutation({
    mutationFn: ({
      addressId,
      role,
    }: {
      addressId: string;
      role: 'billing' | 'shipping';
    }) =>
      customersService.updateAddress(customerId!, addressId, {
        isDefaultBilling: role === 'billing' ? true : undefined,
        isDefaultShipping: role === 'shipping' ? true : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', 'addresses', customerId] });
    },
  });

  if (!customerId) return null;

  const addresses = addressesQuery.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-muted-foreground">
          Addresses drive invoice "Bill to" / "Ship to" defaults and the
          GST intra-vs-inter-state classification.
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus className="h-3 w-3" />
          Add address
        </Button>
      </div>

      {creating && (
        <AddressForm
          customerId={customerId}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <AddressForm
          customerId={customerId}
          editing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {addressesQuery.isLoading ? (
        <div className="text-[12px] text-muted-foreground">Loading…</div>
      ) : addresses.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/70 px-3 py-6 text-center text-[12.5px] text-muted-foreground">
          No addresses yet. Add one to enable invoice + dispatch defaults.
        </div>
      ) : (
        <div className="space-y-2">
          {addresses.map((a) => (
            <AddressCard
              key={a.id}
              address={a}
              onEdit={() => setEditing(a)}
              onDelete={() => remove.mutate(a.id)}
              onSetBillingDefault={() =>
                setDefault.mutate({ addressId: a.id, role: 'billing' })
              }
              onSetShippingDefault={() =>
                setDefault.mutate({ addressId: a.id, role: 'shipping' })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddressCard({
  address,
  onEdit,
  onDelete,
  onSetBillingDefault,
  onSetShippingDefault,
}: {
  address: CustomerAddress;
  onEdit: () => void;
  onDelete: () => void;
  onSetBillingDefault: () => void;
  onSetShippingDefault: () => void;
}) {
  const isBillingRole = address.role === 'BILLING' || address.role === 'BOTH';
  const isShippingRole = address.role === 'SHIPPING' || address.role === 'BOTH';

  return (
    <div className="flex items-start gap-3 rounded-md border border-border/70 bg-card p-3">
      <span
        aria-hidden="true"
        className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary"
      >
        <MapPin className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[13px] font-medium text-foreground">
            {address.label || `${address.city}, ${address.state}`}
          </span>
          <Badge variant="muted" className="text-[10px]">
            {address.role.toLowerCase()}
          </Badge>
          {address.isDefaultBilling && (
            <Badge variant="muted" className="text-[10px]">
              <ReceiptText className="mr-0.5 h-2.5 w-2.5 inline" />
              default bill
            </Badge>
          )}
          {address.isDefaultShipping && (
            <Badge variant="muted" className="text-[10px]">
              <Truck className="mr-0.5 h-2.5 w-2.5 inline" />
              default ship
            </Badge>
          )}
        </div>

        <div className="text-[12.5px] leading-relaxed text-foreground">
          {address.contactName && (
            <span className="font-medium">{address.contactName}</span>
          )}
          {address.contactName && <span> · </span>}
          {address.line1}
          {address.line2 ? `, ${address.line2}` : ''}
          {`, ${address.city}, ${address.state}`}
          {address.postalCode ? ` ${address.postalCode}` : ''}
          {`, ${address.country}`}
        </div>

        {address.gstin && (
          <div className="font-mono text-[11px] text-muted-foreground">
            GSTIN {address.gstin}
          </div>
        )}
        {address.contactPhone && (
          <div className="text-[11px] text-muted-foreground">
            {address.contactPhone}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {isBillingRole && !address.isDefaultBilling && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={onSetBillingDefault}
              >
                <ReceiptText className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Set as default billing</TooltipContent>
          </Tooltip>
        )}
        {isShippingRole && !address.isDefaultShipping && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={onSetShippingDefault}
              >
                <Truck className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Set as default shipping</TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function AddressForm({
  customerId,
  editing,
  onClose,
}: {
  customerId: string;
  editing?: CustomerAddress;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!editing;
  const [form, setForm] = useState<AddressCreatePayload>({
    label: editing?.label ?? '',
    role: editing?.role ?? 'BOTH',
    contactName: editing?.contactName ?? '',
    contactPhone: editing?.contactPhone ?? '',
    gstin: editing?.gstin ?? '',
    line1: editing?.line1 ?? '',
    line2: editing?.line2 ?? '',
    city: editing?.city ?? '',
    state: editing?.state ?? '',
    postalCode: editing?.postalCode ?? '',
    country: editing?.country ?? 'IN',
    isDefaultBilling: editing?.isDefaultBilling ?? false,
    isDefaultShipping: editing?.isDefaultShipping ?? false,
  });

  // Re-hydrate when the editing target changes (operator opens different rows).
  useEffect(() => {
    if (!editing) return;
    setForm({
      label: editing.label ?? '',
      role: editing.role,
      contactName: editing.contactName ?? '',
      contactPhone: editing.contactPhone ?? '',
      gstin: editing.gstin ?? '',
      line1: editing.line1,
      line2: editing.line2 ?? '',
      city: editing.city,
      state: editing.state,
      postalCode: editing.postalCode ?? '',
      country: editing.country,
      isDefaultBilling: editing.isDefaultBilling,
      isDefaultShipping: editing.isDefaultShipping,
    });
  }, [editing]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: AddressCreatePayload = {
        ...form,
        label: emptyToUndef(form.label),
        contactName: emptyToUndef(form.contactName),
        contactPhone: emptyToUndef(form.contactPhone),
        gstin: form.gstin ? form.gstin.toUpperCase() : undefined,
        line2: emptyToUndef(form.line2),
        postalCode: emptyToUndef(form.postalCode),
        state: form.state.toUpperCase(),
      };
      if (isEdit && editing) {
        const updatePayload: AddressUpdatePayload = payload;
        return customersService.updateAddress(customerId, editing.id, updatePayload);
      }
      return customersService.createAddress(customerId, payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Address updated' : 'Address added');
      qc.invalidateQueries({ queryKey: ['customers', 'addresses', customerId] });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save address'),
  });

  const valid = form.line1.trim() && form.city.trim() && form.state.trim();

  return (
    <div className="rounded-md border border-border/70 bg-card p-3.5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {isEdit ? 'Edit address' : 'New address'}
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <Field className="col-span-4" id="label" label="Label" optional>
          <Input
            id="label"
            value={form.label ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="HQ / Mumbai warehouse"
            className="h-8 text-[12.5px]"
          />
        </Field>
        <Field className="col-span-4" id="role" label="Role">
          <Select
            value={form.role ?? 'BOTH'}
            onValueChange={(v) => setForm((f) => ({ ...f, role: v as CustomerAddressRole }))}
          >
            <SelectTrigger id="role" className="h-8 text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BOTH">Both (bill + ship)</SelectItem>
              <SelectItem value="BILLING">Billing only</SelectItem>
              <SelectItem value="SHIPPING">Shipping only</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field className="col-span-4" id="gstin" label="GSTIN" hint="Overrides customer GSTIN if set">
          <Input
            id="gstin"
            value={form.gstin ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))
            }
            placeholder="29AAAAA0000A1Z5"
            maxLength={15}
            className="h-8 font-mono text-[12.5px]"
          />
        </Field>

        <Field className="col-span-6" id="contactName" label="Contact name" optional>
          <Input
            id="contactName"
            value={form.contactName ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
            placeholder="Receiver / billing contact"
            className="h-8 text-[12.5px]"
          />
        </Field>
        <Field className="col-span-6" id="contactPhone" label="Contact phone" optional>
          <Input
            id="contactPhone"
            value={form.contactPhone ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
            placeholder="+91 98765 43210"
            className="h-8 text-[12.5px]"
          />
        </Field>

        <Field className="col-span-12" id="line1" label="Address line 1" required>
          <Input
            id="line1"
            value={form.line1}
            onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))}
            placeholder="Plot 14, Industrial Area Phase 2"
            className="h-8 text-[12.5px]"
            required
          />
        </Field>
        <Field className="col-span-12" id="line2" label="Address line 2" optional>
          <Input
            id="line2"
            value={form.line2 ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, line2: e.target.value }))}
            placeholder="Near Metro pillar 42"
            className="h-8 text-[12.5px]"
          />
        </Field>

        <Field className="col-span-5" id="city" label="City" required>
          <Input
            id="city"
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            placeholder="Mumbai"
            className="h-8 text-[12.5px]"
            required
          />
        </Field>
        <Field className="col-span-3" id="state" label="State" required hint="2-letter code (MH/KA/...)">
          <Input
            id="state"
            value={form.state}
            onChange={(e) => setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))}
            placeholder="MH"
            maxLength={3}
            className="h-8 font-mono text-[12.5px]"
            required
          />
        </Field>
        <Field className="col-span-2" id="postalCode" label="PIN" optional>
          <Input
            id="postalCode"
            value={form.postalCode ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
            placeholder="400001"
            maxLength={10}
            className="h-8 font-mono text-[12.5px]"
          />
        </Field>
        <Field className="col-span-2" id="country" label="Country">
          <Input
            id="country"
            value={form.country ?? 'IN'}
            onChange={(e) =>
              setForm((f) => ({ ...f, country: e.target.value.toUpperCase() }))
            }
            maxLength={2}
            className="h-8 font-mono text-[12.5px]"
          />
        </Field>

        <div className="col-span-12 flex flex-wrap items-center gap-3 border-t border-border/60 pt-3 text-[12px]">
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={form.isDefaultBilling ?? false}
              onChange={(e) =>
                setForm((f) => ({ ...f, isDefaultBilling: e.target.checked }))
              }
            />
            <ReceiptText className="h-3 w-3 text-muted-foreground" />
            <span>Default billing</span>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={form.isDefaultShipping ?? false}
              onChange={(e) =>
                setForm((f) => ({ ...f, isDefaultShipping: e.target.checked }))
              }
            />
            <Truck className="h-3 w-3 text-muted-foreground" />
            <span>Default shipping</span>
          </label>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
          {mutation.isPending ? (isEdit ? 'Saving…' : 'Adding…') : isEdit ? 'Save address' : 'Add address'}
        </Button>
      </div>
    </div>
  );
}

function Field({
  className,
  id,
  label,
  required,
  optional,
  hint,
  children,
}: {
  className?: string;
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1', className)}>
      <label htmlFor={id} className="text-[11px] text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
        {optional && <span className="text-muted-foreground/60"> · optional</span>}
        {hint && <span className="text-muted-foreground/60"> · {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function emptyToUndef(s: string | undefined | null): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}
